"use client";

import { BookOpen, ChevronDown, Copy, Search, Upload } from "lucide-react";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { toast } from "sonner";
import { BibliographyExportMenu } from "@/components/bibliography-export-menu";
import { BibliographyImportDialog } from "@/components/bibliography-import-dialog";
import { AddSourcePanel } from "@/components/bibliography-studio/add-source-panel";
import { BackupControls } from "@/components/bibliography-studio/backup-controls";
import { Inspector } from "@/components/bibliography-studio/inspector";
import { ProjectList } from "@/components/bibliography-studio/project-list";
import { ReferenceRow } from "@/components/bibliography-studio/reference-row";
import { SelectionBar } from "@/components/bibliography-studio/selection-bar";
import {
  ExplorePanel,
  StudioChrome,
  type StudioChromeStrings,
  StyleGuideCard,
} from "@/components/bibliography-studio/studio-chrome";
import { useMediaQuery } from "@/components/bibliography-studio/use-media-query";
import {
  isSortMode,
  type StudioStore,
  useStudioStore,
} from "@/components/bibliography-studio/use-studio-store";
import { CitationClusterComposer } from "@/components/citation-cluster-composer";
import {
  type BibStrings,
  useBibStrings,
} from "@/components/citation-tool/tool-strings";
import { EditSavedCitationDialog } from "@/components/edit-saved-citation-dialog";
import {
  MixedStyleNotice,
  RestyleBibliography,
} from "@/components/restyle-bibliography";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CITATION_STYLES } from "@/data/citation-styles";
import {
  type CitationSortMode,
  getStyleBreakdown,
  removeCitation,
  reorderCitation,
  type SavedCitation,
  searchCitations,
  sortCitations,
  transferCitations,
} from "@/lib/bibliography";
import { writeToClipboardOrThrow } from "@/lib/clipboard";
import { t } from "@/lib/i18n/interpolate";
import { DEFAULT_OUTPUT_LOCALE } from "@/lib/output-locales";

interface BibliographyStudioProps {
  locale: string;
  /** Citation output language for new sources on localized pages. */
  defaultOutputLocale?: string;
  /** `?source=` deep link: opens the add panel and cites it on mount. */
  source?: string;
  /** The page's h1, shown in the studio's own top bar. */
  title: string;
  chrome: StudioChromeStrings;
  /** Share/embed menu rendered by the server page. */
  share?: ReactNode;
}

function getStyleName(slug: string): string {
  const style = CITATION_STYLES.find((s) => s.slug === slug);
  return style?.shortName ?? style?.name ?? slug.toUpperCase();
}

function sortOptions(
  bib: BibStrings
): { value: CitationSortMode; label: string }[] {
  return [
    { value: "author-asc", label: bib.sortAuthor },
    { value: "date-desc", label: bib.sortNewest },
    { value: "year-asc", label: bib.sortOldest },
    { value: "manual", label: bib.sortManual },
  ];
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  return (
    target.isContentEditable ||
    ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)
  );
}

/** Selection state plus the handful of set operations the list and bar need. */
function useSelection() {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  function toggle(id: string) {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function toggleAll(ids: string[], allSelected: boolean) {
    setSelectedIds((current) => {
      const next = new Set(current);
      for (const id of ids) {
        if (allSelected) {
          next.delete(id);
        } else {
          next.add(id);
        }
      }
      return next;
    });
  }

  const clear = useCallback(() => setSelectedIds(new Set()), []);

  return { selectedIds, toggle, toggleAll, clear };
}

function EmptyProject({
  loaded,
  onImport,
}: {
  loaded: boolean;
  onImport: () => void;
}) {
  const bib = useBibStrings();
  if (!loaded) {
    return null;
  }
  return (
    <div className="flex flex-col items-center gap-2 py-10 text-center">
      <p className="font-medium text-foreground">{bib.studioEmptyTitle}</p>
      <p className="max-w-md text-muted-foreground text-sm leading-relaxed">
        {bib.studioEmptyBody}
      </p>
      <Button className="mt-2" onClick={onImport} size="sm" variant="outline">
        <Upload className="size-4" />
        {bib.importTitle}
      </Button>
    </div>
  );
}

interface SideColumnProps {
  inspected: boolean;
  inspector: ReactNode;
  chrome: StudioChromeStrings;
  styleSlug: string;
  styleName: string;
}

/** The wide-screen right column: the inspector, or the rest of the site when nothing is selected. */
function SideColumn({
  inspected,
  inspector,
  chrome,
  styleSlug,
  styleName,
}: SideColumnProps) {
  const bib = useBibStrings();
  return (
    <aside
      aria-label={inspected ? bib.studioInspectorTitle : bib.studioExplore}
      className="hidden min-h-0 overflow-y-auto border-border/70 border-s bg-muted/10 xl:block"
    >
      {inspected ? (
        inspector
      ) : (
        <ExplorePanel
          strings={chrome}
          styleName={styleName}
          styleSlug={styleSlug}
        />
      )}
    </aside>
  );
}

interface ProjectSwitcherProps {
  store: StudioStore;
  onChanged: () => void;
}

/** Narrow-screen stand-in for the sidebar: the project name opens the project list. */
function ProjectSwitcher({ store, onChanged }: ProjectSwitcherProps) {
  const bib = useBibStrings();
  const [open, setOpen] = useState(false);
  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <Button
          className="max-w-full justify-start gap-2 px-2"
          size="sm"
          title={bib.studioSwitchProject}
          variant="ghost"
        >
          <BookOpen className="size-4 shrink-0 text-muted-foreground" />
          <span className="truncate font-semibold text-base">
            {store.activeBib?.name}
          </span>
          <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-0">
        <ProjectList
          activeId={store.activeBib?.id ?? null}
          bibliographies={store.bibliographies}
          onChanged={onChanged}
          onPicked={() => setOpen(false)}
        />
      </PopoverContent>
    </Popover>
  );
}

/**
 * The Bibliography Studio: projects on the left, the reference list as the document
 * in the middle, one reference's details on the right. The generator is embedded at
 * the top of the workspace so adding a source never leaves the page.
 */
export function BibliographyStudio({
  locale,
  defaultOutputLocale,
  source,
  title,
  chrome,
  share,
}: BibliographyStudioProps) {
  const bib = useBibStrings();
  const store = useStudioStore();
  const { citations, activeBib, bibliographies, sortMode } = store;
  const wide = useMediaQuery("(min-width: 1280px)");
  const desktop = useMediaQuery("(min-width: 1024px)");
  const selection = useSelection();
  const clearSelection = selection.clear;
  const searchRef = useRef<HTMLInputElement>(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [addOpen, setAddOpen] = useState(Boolean(source));
  const [inspectId, setInspectId] = useState<string | null>(null);
  const [editCitation, setEditCitation] = useState<SavedCitation | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteSelectedOpen, setDeleteSelectedOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [clusterOpen, setClusterOpen] = useState(false);

  // First load of an empty project: open the generator so the first action is obvious.
  const openedForEmpty = useRef(false);
  useEffect(() => {
    if (store.loaded && citations.length === 0 && !openedForEmpty.current) {
      openedForEmpty.current = true;
      setAddOpen(true);
    }
  }, [store.loaded, citations.length]);

  // `/` focuses search, Escape clears selection and closes the inspector.
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === "/" && !isTypingTarget(event.target)) {
        event.preventDefault();
        searchRef.current?.focus();
      } else if (event.key === "Escape" && !isTypingTarget(event.target)) {
        clearSelection();
        setInspectId(null);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [clearSelection]);

  const filtered = sortCitations(
    searchCitations(citations, searchQuery),
    sortMode
  );
  const visibleIds = filtered.map((c) => c.id);
  const allVisibleSelected =
    filtered.length > 0 &&
    visibleIds.every((id) => selection.selectedIds.has(id));
  const selectedCitations = filtered.filter((c) =>
    selection.selectedIds.has(c.id)
  );
  const transferTargets = bibliographies.filter((b) => b.id !== activeBib?.id);
  const inspected = citations.find((c) => c.id === inspectId) ?? null;
  const breakdown = getStyleBreakdown(citations);
  const projectStyle = activeBib?.preferredStyle ?? breakdown.dominant ?? "apa";
  const preferredLocale = activeBib?.preferredLocale ?? DEFAULT_OUTPUT_LOCALE;
  const clusterStyle =
    activeBib?.preferredStyle ?? selectedCitations[0]?.style ?? "apa";
  const clusterLocale =
    activeBib?.preferredLocale ??
    selectedCitations[0]?.lang ??
    DEFAULT_OUTPUT_LOCALE;
  const hasCitations = citations.length > 0;

  function handleProjectChanged() {
    selection.clear();
    setInspectId(null);
    setSearchQuery("");
    store.reload();
  }

  function handleSortChange(value: string) {
    if (isSortMode(value)) {
      store.setSortMode(value);
    }
  }

  function handleTransfer(targetId: string, mode: "copy" | "move") {
    if (!activeBib) {
      return;
    }
    const result = transferCitations(
      activeBib.id,
      targetId,
      [...selection.selectedIds],
      mode
    );
    toast.success(
      t(mode === "move" ? bib.moveComplete : bib.copyComplete, {
        count: result.transferred,
        skipped: result.skipped,
      })
    );
    selection.clear();
    store.reload();
  }

  function handleDeleteConfirm() {
    if (!deleteId) {
      return;
    }
    removeCitation(deleteId);
    if (inspectId === deleteId) {
      setInspectId(null);
    }
    setDeleteId(null);
    store.reload();
    toast.success(bib.deleted);
  }

  function handleDeleteSelected() {
    const ids = [...selection.selectedIds];
    for (const id of ids) {
      removeCitation(id);
    }
    if (inspectId && selection.selectedIds.has(inspectId)) {
      setInspectId(null);
    }
    selection.clear();
    setDeleteSelectedOpen(false);
    store.reload();
    toast.success(t(bib.studioDeletedMany, { count: ids.length }));
  }

  function handleMove(id: string, direction: "up" | "down") {
    if (activeBib && reorderCitation(activeBib.id, id, direction)) {
      store.reload();
    }
  }

  async function handleCopyAll() {
    try {
      await writeToClipboardOrThrow(
        filtered.map((c) => c.citation).join("\n\n")
      );
      toast.success(bib.copiedAll);
    } catch {
      toast.error(bib.copyFailed);
    }
  }

  const projectStyleName = getStyleName(projectStyle);
  const inspector = (
    <Inspector
      citation={inspected}
      onClose={wide ? undefined : () => setInspectId(null)}
      onDelete={() => inspected && setDeleteId(inspected.id)}
      onEdit={() => inspected && setEditCitation(inspected)}
      onSaved={store.reload}
      styleName={inspected ? getStyleName(inspected.style) : ""}
    />
  );

  return (
    <div className="flex h-dvh min-h-0 flex-col bg-card" data-studio>
      <StudioChrome
        locale={locale}
        share={share}
        strings={chrome}
        styleName={projectStyleName}
        styleSlug={projectStyle}
        title={title}
      />
      <div className="grid min-h-0 flex-1 lg:grid-cols-[15rem_minmax(0,1fr)] xl:grid-cols-[15rem_minmax(0,1fr)_21rem]">
        {/* Sidebar */}
        <aside
          aria-label={bib.studioProjects}
          className="hidden min-h-0 flex-col overflow-y-auto border-border/70 border-e bg-muted/20 lg:flex"
        >
          <ProjectList
            activeId={activeBib?.id ?? null}
            bibliographies={bibliographies}
            onChanged={handleProjectChanged}
          />
          <div className="px-3 pb-3">
            <StyleGuideCard
              styleName={projectStyleName}
              styleSlug={projectStyle}
            />
          </div>
          <BackupControls onRestored={handleProjectChanged} />
        </aside>

        {/* Workspace */}
        <section
          aria-labelledby="studio-project-title"
          className="flex min-h-0 min-w-0 flex-col overflow-y-auto"
        >
          <header className="sticky top-0 z-10 flex flex-wrap items-center gap-2 border-border/70 border-b bg-card/95 px-4 py-3 backdrop-blur-sm sm:px-5">
            <div className="flex min-w-[14rem] flex-1 items-center gap-2">
              {desktop ? (
                <h2
                  className="truncate font-semibold text-foreground text-lg"
                  id="studio-project-title"
                >
                  {activeBib?.name ?? bib.studioTitle}
                </h2>
              ) : (
                <div className="min-w-0" id="studio-project-title">
                  <ProjectSwitcher
                    onChanged={handleProjectChanged}
                    store={store}
                  />
                </div>
              )}
              <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-muted-foreground text-xs tabular-nums">
                {t(bib.studioReferenceCount, { count: citations.length })}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              {hasCitations ? (
                <RestyleBibliography
                  citations={citations}
                  getStyleName={getStyleName}
                  onRestyled={store.reload}
                  preferredLocale={preferredLocale}
                />
              ) : null}
              <Button
                onClick={() => setImportOpen(true)}
                size="sm"
                title={bib.importLabel}
                variant="outline"
              >
                <Upload className="size-4" />
                <span className="sr-only sm:not-sr-only">{bib.import}</span>
              </Button>
              {hasCitations ? (
                <>
                  <Button
                    onClick={handleCopyAll}
                    size="sm"
                    title={bib.copyAllTitle}
                    variant="outline"
                  >
                    <Copy className="size-4" />
                    <span className="sr-only sm:not-sr-only">
                      {bib.copyAll}
                    </span>
                  </Button>
                  <BibliographyExportMenu
                    bibliographyName={activeBib?.name}
                    citations={filtered}
                    locale={locale}
                    responsiveLabel={false}
                    variant="default"
                  />
                </>
              ) : null}
            </div>
          </header>

          <div className="mx-auto w-full max-w-4xl flex-1 space-y-4 px-4 py-4 sm:px-5">
            {activeBib ? (
              <AddSourcePanel
                defaultOutputLocale={defaultOutputLocale}
                defaultStyle={projectStyle}
                onSaved={store.reload}
                onToggle={() => setAddOpen((open) => !open)}
                open={addOpen}
                project={activeBib}
                source={source}
              />
            ) : null}

            {hasCitations ? (
              <>
                <MixedStyleNotice
                  citations={citations}
                  getStyleName={getStyleName}
                />
                <div className="flex flex-wrap items-center gap-2">
                  <label className="flex cursor-pointer items-center gap-2 px-1 text-sm">
                    <input
                      aria-label={bib.selectAllVisible}
                      checked={allVisibleSelected}
                      className="size-4 accent-primary"
                      onChange={() =>
                        selection.toggleAll(visibleIds, allVisibleSelected)
                      }
                      type="checkbox"
                    />
                    <span className="sr-only sm:not-sr-only">
                      {bib.selectAllVisible}
                    </span>
                  </label>
                  <div className="relative min-w-[160px] flex-1">
                    <Search className="absolute start-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      className="h-9 ps-9"
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder={bib.searchPlaceholder}
                      ref={searchRef}
                      title={bib.studioSearchShortcut}
                      type="search"
                      value={searchQuery}
                    />
                  </div>
                  <Select onValueChange={handleSortChange} value={sortMode}>
                    <SelectTrigger
                      aria-label={bib.sortTitle}
                      className="h-9 w-[170px]"
                      size="sm"
                      title={bib.sortOrder}
                    >
                      <SelectValue placeholder={bib.sortBy} />
                    </SelectTrigger>
                    <SelectContent>
                      {sortOptions(bib).map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {filtered.length === 0 ? (
                  <p className="py-10 text-center text-muted-foreground text-sm">
                    {bib.emptySearch}
                  </p>
                ) : (
                  <ul className="-mx-4 divide-y divide-border/60 border-border/60 border-y sm:-mx-5">
                    {filtered.map((item, index) => (
                      <ReferenceRow
                        active={item.id === inspectId}
                        checked={selection.selectedIds.has(item.id)}
                        index={index}
                        isFirst={citations[0]?.id === item.id}
                        isLast={citations.at(-1)?.id === item.id}
                        item={item}
                        key={item.id}
                        manual={sortMode === "manual"}
                        onDelete={() => setDeleteId(item.id)}
                        onEdit={() => setEditCitation(item)}
                        onMove={(direction) => handleMove(item.id, direction)}
                        onOpen={() =>
                          setInspectId((current) =>
                            current === item.id && wide ? null : item.id
                          )
                        }
                        onToggle={() => selection.toggle(item.id)}
                        styleName={getStyleName(item.style)}
                      />
                    ))}
                  </ul>
                )}

                <SelectionBar
                  count={selection.selectedIds.size}
                  onClear={selection.clear}
                  onCluster={() => setClusterOpen(true)}
                  onDelete={() => setDeleteSelectedOpen(true)}
                  onTransfer={handleTransfer}
                  targets={transferTargets}
                />
              </>
            ) : (
              <EmptyProject
                loaded={store.loaded}
                onImport={() => setImportOpen(true)}
              />
            )}
          </div>
        </section>

        <SideColumn
          chrome={chrome}
          inspected={inspected !== null}
          inspector={inspector}
          styleName={projectStyleName}
          styleSlug={projectStyle}
        />
      </div>

      {/* Inspector sheet on narrower screens */}
      <Dialog
        onOpenChange={(open) => !open && setInspectId(null)}
        open={!wide && inspected !== null}
      >
        <DialogContent
          className="flex max-h-[85vh] flex-col gap-0 p-0 sm:max-w-lg"
          showCloseButton={false}
        >
          <DialogTitle className="sr-only">
            {bib.studioInspectorTitle}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {bib.studioInspectorEmpty}
          </DialogDescription>
          {inspector}
        </DialogContent>
      </Dialog>

      <CitationClusterComposer
        citations={selectedCitations}
        lang={clusterLocale}
        onOpenChange={setClusterOpen}
        open={clusterOpen}
        style={clusterStyle}
        styleName={getStyleName(clusterStyle)}
      />

      <BibliographyImportDialog
        citations={citations}
        lang={preferredLocale}
        onImported={store.reload}
        onOpenChange={setImportOpen}
        open={importOpen}
        style={projectStyle}
      />

      <EditSavedCitationDialog
        citation={editCitation}
        onClose={() => setEditCitation(null)}
        onSaved={store.reload}
        preferredLocale={preferredLocale}
      />

      <Dialog onOpenChange={() => setDeleteId(null)} open={!!deleteId}>
        <DialogContent className="sm:max-w-md" showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>{bib.deleteConfirm}</DialogTitle>
            <DialogDescription>{bib.deleteBody}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setDeleteId(null)} variant="outline">
              {bib.cancel}
            </Button>
            <Button onClick={handleDeleteConfirm} variant="destructive">
              {bib.delete}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog onOpenChange={setDeleteSelectedOpen} open={deleteSelectedOpen}>
        <DialogContent className="sm:max-w-md" showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>{bib.studioDeleteSelected}</DialogTitle>
            <DialogDescription>
              {t(bib.studioDeleteSelectedBody, {
                count: selection.selectedIds.size,
                name: activeBib?.name ?? "",
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              onClick={() => setDeleteSelectedOpen(false)}
              variant="outline"
            >
              {bib.cancel}
            </Button>
            <Button onClick={handleDeleteSelected} variant="destructive">
              {bib.delete}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
